import { useState, useEffect } from 'react';
import { useRoadmapService } from '../../../../services';

interface ProjectOption {
  value: string;
  label: string;
  project_start_date?: string;
  project_end_date?: string;
  isDisabled?: boolean;
  error?: string;
}

interface DependencyState {
  _uid: string;
  project_id: string;
  project_name: string;
  start_date: any;
  end_date: any;
  dependencies: any[];
}

interface UseDependencyManagementProps {
  projects: any[];
  currentRoadmapId: string;
  singleRoadmapRes: any;
  simulation: boolean;
}

export const useDependencyManagement = ({
  projects,
  currentRoadmapId,
  singleRoadmapRes,
  simulation
}: UseDependencyManagementProps) => {
  const [dependenciesState, setDependenciesState] = useState<DependencyState[]>([]);
  const [editDep, setEditDep] = useState<{depId: string, linkIdx: number} | null>(null);
  const [editDepProject, setEditDepProject] = useState('');
  const [editDepDependsOn, setEditDepDependsOn] = useState<string[]>([]);
  const [isAddingDep, setIsAddingDep] = useState(false);
  const [addDepProject, setAddDepProject] = useState('');
  const [addDepDependsOn, setAddDepDependsOn] = useState<string[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  
  const { updateRoadmap, fetchSingleRoadmap } = useRoadmapService();

  const findProjectById = (id: string) => {
    const fromRoadmap = singleRoadmapRes?.projects?.find((el: any) => el.project_id === id);
    if (fromRoadmap) return fromRoadmap;
    return projects?.find((el: any) => el.project_id === id);
  };

  const normalizeDependencyItem = (depItem: any) => {
    const projectId = depItem?.project_id || depItem?.value || depItem;
    const projectData = findProjectById(projectId);

    const projectName = depItem?.project_name || depItem?.label || projectData?.project_name || '';
    const projectStart = depItem?.project_start_date || projectData?.start_date || '';
    const projectEnd = depItem?.project_end_date || projectData?.end_date || '';

    return {
      project_id: projectId,
      project_name: projectName,
      project_start_date: projectStart,
      project_end_date: projectEnd,
      label: projectName,
      value: projectId,
      invalid_dependency: depItem.invalid_dependency,
      type: depItem.type,
      entity_type: depItem.entity_type
    };
  };

  const normalizeDependenciesArray = (depArray: any[]) => {
    if (!Array.isArray(depArray)) return [] as any[];
    return depArray.map((d: any) => normalizeDependencyItem(d));
  };

  useEffect(() => {
    const depMap = new Map();
    projects.forEach((project) => {
      if (Array.isArray(project.dependencies) && project.dependencies.length > 0) {
        if (!depMap.has(project.project_id)) {
          depMap.set(project.project_id, {
            _uid: `${project.project_id}_${new Date().getTime()}`,
            project_id: project.project_id,
            project_name: project.project_name,
            start_date: project.start_date,
            end_date: project.end_date,
            dependencies: normalizeDependenciesArray(project.dependencies),
            type: project.type,
            entity_type: project.entity_type,
          });
        } else {
          const existing = depMap.get(project.project_id).dependencies || [];
          depMap.get(project.project_id).dependencies = [
            ...existing,
            ...normalizeDependenciesArray(project.dependencies),
          ];
        }
      }
    });
    setDependenciesState(Array.from(depMap.values()));
  }, [projects]);

  // Helper function to generate dependencies payload
  const getDependenciesPayload = (dependenciesArr: DependencyState[], roadmapData: any) => {
    return dependenciesArr
      .filter(dep => dep.project_id && dep.dependencies.length)
      .map(dep => {
        const project = roadmapData.projects.find((el: any) => el.project_id === dep.project_id);
        return {
          dependency: dep.dependencies.map((d: any) => {
            const depProject = roadmapData.projects.find((el: any) => el.project_id === (d?.project_id || d?.value));
            const projectId = d?.project_id || d?.value;
            const projectName = d?.project_name || d?.label || depProject?.project_name || '';
            const projectStart = d?.project_start_date || depProject?.start_date || '';
            const projectEnd = d?.project_end_date || depProject?.end_date || '';

            return {
              label: projectName,
              project_end_date: projectEnd,
              project_id: projectId,
              project_start_date: projectStart,
              value: projectId,
            };
          }),
          project_end_date: project?.end_date,
          project_id: dep.project_id,
          project_start_date: project?.start_date,
        };
      });
  };

  // Get filtered project list for dependencies
  const getFilterProjectList = (projectId: string): ProjectOption[] => {
    const optionsArr: ProjectOption[] = [];
    const referenceProject = projects.find(project => project.project_id === projectId);

    if (!referenceProject) return optionsArr;

    const { start_date: refStartDate, end_date: refEndDate } = referenceProject;

    projects.forEach((element: any) => {
      if (element.project_id === projectId) return;

      // Backend conditions:
      // 1. Dependent project cannot start before the project it depends on
      // 2. Dependent project cannot end before the project it depends on
      const dependencyCanStartFirst = new Date(element.start_date) < new Date(refStartDate);
      const dependencyMustEndBeforeOrSame = new Date(element.end_date) < new Date(refEndDate);
      const isEnabled = dependencyCanStartFirst && dependencyMustEndBeforeOrSame;

      let errorText = "";
      if (!isEnabled) {
        if (!dependencyCanStartFirst && !dependencyMustEndBeforeOrSame) {
          errorText = "Dependency must start on or before and end on or before this project";
        } else if (!dependencyCanStartFirst) {
          errorText = "Dependency must start on or before this project";
        } else if (!dependencyMustEndBeforeOrSame) {
          errorText = "Dependency must end on or before this project";
        }
      }

      optionsArr.push({
        value: element.project_id,
        label: element.project_name,
        project_start_date: element.start_date,
        project_end_date: element.end_date,
        isDisabled: !isEnabled,
        error: errorText
      });
    });

    return optionsArr.sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled));
  };

  // Get project list for selection
  const getProjectList = (): ProjectOption[] => {
    const optionsArr: ProjectOption[] = [];
    projects.forEach((project: any) => {
      // if (
      //   !dependenciesState.map(dep => dep.project_id).includes(project.project_id) ||
      //   project.project_id === editDepProject
      // ) 
      {
        optionsArr.push({
          value: project.project_id,
          label: project.project_name,
          project_start_date: project.start_date,
          project_end_date: project.end_date,
        });
      }
    });
    return optionsArr;
  };

  // Update dependency
  const handleUpdateDependency = async () => {
    if (!editDep || !editDepProject || !currentRoadmapId) return;
    setIsActionLoading(true);
    try {
      const currentDeps = [...dependenciesState];
      const updatedDeps = currentDeps.map(dep => {
        if (dep.project_id === editDepProject) {
          return {
            ...dep,
            dependencies: editDepDependsOn.map((id: string) => {
              const project = singleRoadmapRes.projects.find((el: any) => el.project_id === id);
              return {
                project_id: id,
                project_name: project?.project_name || '',
                project_start_date: project?.start_date || '',
                project_end_date: project?.end_date || '',
                label: project?.project_name || '',
                value: id,
              };
            }),
          };
        }
        return dep;
      }).filter(dep => dep.dependencies.length > 0);

      const dependenciesPayload = getDependenciesPayload(updatedDeps, singleRoadmapRes);
      const payload = {
        name: singleRoadmapRes?.name || '',
        description: singleRoadmapRes?.description || '',
        milestones: singleRoadmapRes?.milestones || [],
        projects: (singleRoadmapRes?.projects || []).map((p: any) => p.project_id),
        dependencies: dependenciesPayload,
      };

      await updateRoadmap(currentRoadmapId, payload);
      if (currentRoadmapId) {
        await fetchSingleRoadmap(currentRoadmapId, false);
      }
      setEditDep(null);
    } catch (error) {
      console.error('Error updating dependency:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Add dependency
  const handleAddDependency = async () => {
    if (!currentRoadmapId || !addDepProject || addDepDependsOn.length === 0) return;
    setIsActionLoading(true);
    try {
      const currentDeps = [...dependenciesState];
      const project = singleRoadmapRes.projects.find((el: any) => el.project_id === addDepProject);
      if (!project) return;

      const newDependencies = addDepDependsOn.map((id: string) => {
        const depProject = singleRoadmapRes.projects.find((el: any) => el.project_id === id);
        return {
          project_id: id,
          project_name: depProject?.project_name || '',
          project_start_date: depProject?.start_date || '',
          project_end_date: depProject?.end_date || '',
          label: depProject?.project_name || '',
          value: id,
        };
      });

      let updatedDeps;
      const existingDepIdx = currentDeps.findIndex(dep => dep.project_id === addDepProject);
      if (existingDepIdx !== -1) {
        // Merge and normalize to ensure all dependency items include dates and names
        currentDeps[existingDepIdx].dependencies = normalizeDependenciesArray([
          ...currentDeps[existingDepIdx].dependencies,
          ...newDependencies,
        ]);
        updatedDeps = currentDeps;
      } else {
        updatedDeps = [
          ...currentDeps,
          {
            _uid: `${addDepProject}_${new Date().getTime()}`,
            project_id: addDepProject,
            project_name: project.project_name,
            start_date: project.start_date,
            end_date: project.end_date,
            dependencies: normalizeDependenciesArray(newDependencies),
          }
        ];
      }

      const dependenciesPayload = getDependenciesPayload(updatedDeps, singleRoadmapRes);
      const payload = {
        name: singleRoadmapRes?.name || '',
        description: singleRoadmapRes?.description || '',
        milestones: singleRoadmapRes?.milestones || [],
        projects: (singleRoadmapRes?.projects || []).map((p: any) => p.project_id),
        dependencies: dependenciesPayload,
      };

      await updateRoadmap(currentRoadmapId, payload);
      if (currentRoadmapId) {
        await fetchSingleRoadmap(currentRoadmapId, false);
      }
      
      setIsAddingDep(false);
      setAddDepProject('');
      setAddDepDependsOn([]);
    } catch (error) {
      console.error('Error adding dependency:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Delete single dependency
  const handleDeleteSingleDependency = async (projectId: string, dependencyId: string) => {
    if (!currentRoadmapId) return;
    setIsActionLoading(true);
    try {
      const currentDeps = [...dependenciesState];
      const updatedDeps = currentDeps.map(dep => {
        if (dep.project_id === projectId) {
          return {
            ...dep,
            // Filter then normalize remaining to ensure complete items
            dependencies: normalizeDependenciesArray(
              dep.dependencies.filter((d: any) => (d?.project_id || d?.value) !== dependencyId)
            )
          };
        }
        return dep;
      }).filter(dep => dep.dependencies.length > 0);

      const dependenciesPayload = getDependenciesPayload(updatedDeps, singleRoadmapRes);
      const payload = {
        name: singleRoadmapRes?.name || '',
        description: singleRoadmapRes?.description || '',
        milestones: singleRoadmapRes?.milestones || [],
        projects: (singleRoadmapRes?.projects || []).map((p: any) => p.project_id),
        dependencies: dependenciesPayload,
      };

      await updateRoadmap(currentRoadmapId, payload);
      if (currentRoadmapId) {
        await fetchSingleRoadmap(currentRoadmapId, false);
      }
    } catch (error) {
      console.error('Error deleting single dependency:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  // Delete all dependencies for a project
  const handleDeleteAllDependencies = async (projectId: string) => {
    if (!currentRoadmapId) return;
    setIsActionLoading(true);
    try {
      const updatedDeps = dependenciesState.filter(dep => dep.project_id !== projectId);
      const dependenciesPayload = getDependenciesPayload(updatedDeps, singleRoadmapRes);
      
      const payload = {
        name: singleRoadmapRes?.name || '',
        description: singleRoadmapRes?.description || '',
        milestones: singleRoadmapRes?.milestones || [],
        projects: (singleRoadmapRes?.projects || []).map((p: any) => p.project_id),
        dependencies: dependenciesPayload,
      };

      await updateRoadmap(currentRoadmapId, payload);
      if (currentRoadmapId) {
        await fetchSingleRoadmap(currentRoadmapId, false);
      }
    } catch (error) {
      console.error('Error deleting all dependencies:', error);
    } finally {
      setIsActionLoading(false);
    }
  };

  return {
    // State
    dependenciesState,
    editDep,
    editDepProject,
    editDepDependsOn,
    isAddingDep,
    addDepProject,
    addDepDependsOn,
    isActionLoading,
    
    // Setters
    setEditDep,
    setEditDepProject,
    setEditDepDependsOn,
    setIsAddingDep,
    setAddDepProject,
    setAddDepDependsOn,
    
    // Functions
    getFilterProjectList,
    getProjectList,
    handleUpdateDependency,
    handleAddDependency,
    handleDeleteSingleDependency,
    handleDeleteAllDependencies,
  };
}; 